import type { TosUpdateWindow } from '@magicschool/business-logic/site-configs';
import { logger } from '@magicschool/logger';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import { SECONDS_IN_HOUR } from 'util/date';
import type { TosUpdateWindowResponse } from '@/app/api/tos-updates/route';

export type ToSBannerStore = {
  setField: SetField<ToSBannerStore>;
  activeBanner: TosUpdateWindow | null;
  showBanner: boolean;
  currentWindowKey: string | null;
  lastFetchTime: number;
  isLoading: boolean;
  load: () => Promise<void>;
  markBannerAcked: (windowKey: string | null) => Promise<void>;
  closeBanner: () => Promise<void>;
  handleUnload: () => Promise<boolean>;
};

const defaultState = {
  currentWindowKey: null,
  showBanner: false,
  activeBanner: null,
  lastFetchTime: 0,
  isLoading: false,
};

export const createToSBannerStoreSlice = createStoreSlice('ToSBannerStoreData', defaultState, ({ set, get, setField }) => ({
  setField,
  load: async () => {
    const { lastFetchTime, isLoading } = get();
    const now = Date.now();

    // Prevent multiple concurrent requests
    if (isLoading) {
      logger.debug('ToS updates already loading, skipping duplicate request');
      return;
    }

    // Cache for 10 hours to prevent excessive calls
    const CACHE_DURATION = 600 * 60 * 1000; // 10 hours in milliseconds
    if (now - lastFetchTime < CACHE_DURATION) {
      logger.debug('ToS updates cached, skipping request', {
        timeSinceLastFetch: now - lastFetchTime,
        cacheDuration: CACHE_DURATION,
      });
      return;
    }

    set({ isLoading: true });

    try {
      await fetch<TosUpdateWindowResponse>('/api/tos-updates', {
        next: { revalidate: SECONDS_IN_HOUR }, // break the cache every hour
        responseErrorHandlers: {
          notFound: () => {
            logger.error('Error fetching tos updates, /api/tos-updates not found.');
          },
          unauthorized: () => {
            logger.error('Error fetching tos updates, unauthorized.');
          },
        },
        onSuccess: async ({ response }) => {
          const tosUpdates = await response.json();
          if (tosUpdates.length >= 1) {
            set({ showBanner: true });
            // we could have multiple ToS updates we explicitly need to get ack'd but one at a time, please.
            set({ currentWindowKey: tosUpdates[0].windowKey });
            set({ activeBanner: tosUpdates[0] });
          } else {
            set({ showBanner: false });
          }
          set({ lastFetchTime: now });
        },
      });
    } catch (err) {
      logger.error('Error fetching tos updates', err);
      // Don't spam logs anymore since we're preventing excessive calls
    } finally {
      set({ isLoading: false });
    }
  },
  markBannerAcked: async (windowKey: string | null) => {
    // i think it's just a local rebuild/refresh problem but sometimes it's null
    const bannerAlreadyShown = sessionStorage.getItem(`tosUpdateAcked_${windowKey}`) === 'true';
    if (bannerAlreadyShown) {
      logger.info('Banner already acknowledged, skipping markBannerAcked', windowKey);
      return;
    }
    if (windowKey) {
      sessionStorage.setItem(`tosUpdateAcked_${windowKey}`, 'true');
      await fetch(`/api/tos-updates`, {
        method: 'POST',
        keepalive: true,
        body: JSON.stringify({ name: `tosUpdate_${windowKey}`, value: 'true' }),
      });
    } else {
      logger.warn('windowKey is null, not marking banner acked', get().activeBanner);
    }
  },
  handleUnload: async () => {
    const { markBannerAcked, showBanner, currentWindowKey } = get();
    if (!showBanner) return Promise.resolve(true);
    await markBannerAcked(currentWindowKey);
    const time = Date.now();
    // future me, if you're reading this, i'm sorry. (that was generated by co-pilot - not bad!)
    // but seriously. without this, the beforeUnload won't ack,even with keepalive
    while (Date.now() - time < 500) {}
    return Promise.resolve(true);
  },
  closeBanner: async () => {
    const { markBannerAcked, handleUnload, currentWindowKey } = get();
    set({ showBanner: false });
    window.removeEventListener('beforeunload', handleUnload, true);
    await markBannerAcked(currentWindowKey);
  },
}));
